import React, { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import ReactQuill from 'react-quill-new';

import { IAdHocTokenReport } from '../../../lib/types/communication.types';
import { TokenIssueKind, findTokenIssues } from './compose-panel.token-highlight';
import { HighlightLayer, HighlightRect } from './quill-token-highlight-overlay.styles';

interface IQuillTokenHighlightOverlayProps {
  quillRef: RefObject<ReactQuill>;
  // Latest server token report; flags unknown / missing-fallback occurrences.
  report?: IAdHocTokenReport;
}

interface IHighlightRect {
  left: number;
  top: number;
  width: number;
  height: number;
  issue: TokenIssueKind;
}

// Absolutely-positioned highlight rectangles over the rich-text email body that
// mark invalid variable tokens, measured from the live editor DOM via Ranges.
export const QuillTokenHighlightOverlay: React.FC<IQuillTokenHighlightOverlayProps> = ({
  quillRef,
  report
}) => {
  const layerRef = useRef<HTMLDivElement>(null);
  const [rects, setRects] = useState<IHighlightRect[]>([]);
  const reportRef = useRef(report);
  reportRef.current = report;

  const recompute = useCallback(() => {
    const editor = quillRef.current && quillRef.current.getEditor();
    const layer = layerRef.current;
    if (!editor || !layer) {
      setRects([]);
      return;
    }

    const layerRect = layer.getBoundingClientRect();
    const next: IHighlightRect[] = [];
    const walker = document.createTreeWalker(editor.root, NodeFilter.SHOW_TEXT);
    let node = walker.nextNode();
    while (node) {
      const textNode = node;
      findTokenIssues(textNode.nodeValue || '', reportRef.current).forEach((mt) => {
        const range = document.createRange();
        range.setStart(textNode, mt.start);
        range.setEnd(textNode, mt.end);
        const lineRects = range.getClientRects();
        for (let i = 0; i < lineRects.length; i += 1) {
          const r = lineRects[i];
          next.push({
            left: r.left - layerRect.left,
            top: r.top - layerRect.top,
            width: r.width,
            height: r.height,
            issue: mt.issue
          });
        }
      });
      node = walker.nextNode();
    }
    setRects(next);
  }, [quillRef]);

  // rAF-coalesced so a burst of text-change / resize events recomputes once.
  const frameRef = useRef<number | null>(null);
  const schedule = useCallback(() => {
    if (frameRef.current != null) return;
    frameRef.current = window.requestAnimationFrame(() => {
      frameRef.current = null;
      recompute();
    });
  }, [recompute]);

  useEffect(() => {
    let cancelled = false;
    let editor: ReturnType<ReactQuill['getEditor']> | null = null;
    let observer: ResizeObserver | null = null;

    // The sibling editor mounts first, but retry a frame in case its Quill
    // instance isn't ready when this effect runs.
    const attach = () => {
      if (cancelled) return;
      const instance = quillRef.current && quillRef.current.getEditor();
      if (!instance) {
        window.requestAnimationFrame(attach);
        return;
      }
      editor = instance;
      editor.on('text-change', schedule);
      observer = new ResizeObserver(schedule);
      observer.observe(editor.root);
      schedule();
    };
    attach();
    window.addEventListener('resize', schedule);

    return () => {
      cancelled = true;
      if (editor) editor.off('text-change', schedule);
      if (observer) observer.disconnect();
      window.removeEventListener('resize', schedule);
      if (frameRef.current != null) window.cancelAnimationFrame(frameRef.current);
    };
  }, [quillRef, schedule]);

  // Re-classify against the latest report without waiting for an edit.
  useEffect(() => {
    schedule();
  }, [report, schedule]);

  return (
    <HighlightLayer ref={layerRef} aria-hidden>
      {rects.map((r, i) => (
        <HighlightRect
          key={i}
          data-issue={r.issue}
          style={{ left: r.left, top: r.top, width: r.width, height: r.height }}
        />
      ))}
    </HighlightLayer>
  );
};
